/**
 * Aspect — Top-down minimap (matches built 3D geometry: walls + doors)
 */

import { wallLength, wallPointAt, paramOnWall, isArcWall, sampleWallPolyline } from "../scene/wall_geometry.js";

/** Visual scale for map + 2D render HUD panels (40% smaller than base). */
const MAP_PANEL_SCALE = 0.6;
const CANVAS_MAX = Math.round(360 * MAP_PANEL_SCALE);
const CANVAS_MIN_W = Math.round(160 * MAP_PANEL_SCALE);
const CANVAS_MIN_H = Math.round(100 * MAP_PANEL_SCALE);
const PAD = 10;
const ROOM_COLORS = ["#3b82f666", "#8b5cf666", "#f59e0b66", "#10b98166", "#ec489966", "#06b6d466"];
const DOOR_COLOR = "#4ade80";
const WINDOW_COLOR = "#60a5fa88";
const WALL_EXT = "rgba(255,255,255,0.95)";
const WALL_INT = "rgba(255,255,255,0.72)";
const FLOOR_FILL = "rgba(30,41,59,0.85)";
const SELECT_COLOR = "#fb923c"; // accent for structure-selected walls/openings

const clamp01 = (t) => Math.min(1, Math.max(0, t));

function wallPoint(wall, t) {
  return wallPointAt(wall, t);
}

function wallLen(w) {
  return wallLength(w);
}

function resolveNamedRooms(room) {
  if (Array.isArray(room?.rooms) && room.rooms.length) return room.rooms;
  return [];
}

/** Floor-centre [x, z] of a built room rect. */
function roomCentroidXZ(room) {
  if (Array.isArray(room?.polygon) && room.polygon.length >= 3) {
    let sx = 0;
    let sz = 0;
    for (const [x, z] of room.polygon) {
      sx += x;
      sz += z;
    }
    return [sx / room.polygon.length, sz / room.polygon.length];
  }
  return [(Number(room?.x) || 0) + (Number(room?.width) || 0) / 2, (Number(room?.z) || 0) + (Number(room?.depth) || 0) / 2];
}

/** Nearest frozen anchor from the original 2D analyze (position + printed m²). */
function anchorForRoom(room, anchors) {
  if (!anchors?.length) return null;
  const [cx, cz] = roomCentroidXZ(room);
  let best = null;
  let bestD = Infinity;
  for (const a of anchors) {
    const d = Math.hypot(cx - (Number(a.cx) || 0), cz - (Number(a.cz) || 0));
    if (d < bestD) {
      bestD = d;
      best = a;
    }
  }
  return best;
}

function polygonAreaM2(poly) {
  if (!Array.isArray(poly) || poly.length < 3) return null;
  let a = 0;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    a += poly[j][0] * poly[i][1] - poly[i][0] * poly[j][1];
  }
  const area = Math.abs(a) / 2;
  return area > 0.2 ? Math.round(area * 10) / 10 : null;
}

function geometricAreaM2(room) {
  const fromPoly = polygonAreaM2(room?.polygon);
  if (fromPoly != null) return fromPoly;
  const w = Number(room?.width) || 0;
  const d = Number(room?.depth) || 0;
  if (w > 0 && d > 0) return Math.round(w * d * 10) / 10;
  return null;
}

/** Printed m² from the 2D render — never parse "Room 1" / "R3" as an area. */
function reportedAreaM2(room, semRoom, anchor) {
  const printed = (text) => {
    const m = String(text ?? "").match(/(\d+(?:\.\d+)?)\s*(m²|m2|sqm)/i);
    if (!m) return null;
    const n = Number(m[1]);
    return Number.isFinite(n) && n > 0 ? Math.round(n * 10) / 10 : null;
  };
  const candidates = [
    anchor?.reportedArea,
    room?.reportedArea,
    semRoom?.reportedArea,
    printed(anchor?.label),
    printed(room?.name),
    printed(room?.label),
    printed(semRoom?.label),
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return Math.round(n * 10) / 10;
  }
  return geometricAreaM2(room);
}

/** Minimap label: always prefer the printed area from the 2D render. */
function roomMinimapLabel(room, semRoom, anchor) {
  const area = reportedAreaM2(room, semRoom, anchor);
  if (area != null) return `${area} m²`;
  const name = anchor?.label ?? room?.label ?? room?.name ?? semRoom?.label;
  if (!name) return "";
  return name.length > 14 ? `${name.slice(0, 13)}…` : name;
}

function semanticsRoomFor(namedRooms, semanticsRooms, ri) {
  if (semanticsRooms[ri]) return semanticsRooms[ri];
  const room = namedRooms[ri];
  const rn = String(room?.name ?? room?.label ?? "")
    .trim()
    .toLowerCase();
  if (!rn) return null;
  return (
    semanticsRooms.find((s) => {
      const sn = String(s.label ?? "")
        .trim()
        .toLowerCase();
      return sn && (sn === rn || sn.includes(rn) || rn.includes(sn));
    }) ?? null
  );
}

export class Minimap {
  /**
   * @param {import('playcanvas').AppBase} app
   * @param {import('playcanvas').Entity} camera
   * @param {import('../interaction/first_person_controls.js').FirstPersonControls} fp
   * @param {HTMLElement} root
   */
  constructor(app, camera, fp, root) {
    this.app = app;
    this.camera = camera;
    this.fp = fp;
    this.canvas = root.querySelector('[data-overlay="minimap-canvas"]');
    this.ctx = this.canvas?.getContext("2d") ?? null;
    this.root = root.querySelector('[data-overlay="minimap"]') ?? root;

    // Registered overlay: rebuilt plan outline drawn over the 2D-render image.
    this.overlayCanvas = root.querySelector('[data-overlay="plan-overlay-canvas"]');
    this.overlayCtx = this.overlayCanvas?.getContext("2d") ?? null;
    this._overlayOn = false;
    this._overlayOpacity = 0.6;

    this.room = null;
    this.namedRooms = [];
    this._semanticsRooms = [];
    this._semanticsAnchors = [];
    this.scale = 1;
    this.minX = 0;
    this.minZ = 0;

    this._overhead = false;
    this.app.on("cam:mode-changed", ({ mode }) => {
      this._overhead = mode === "overhead";
    });

    // Structure-refine selection (edgeId for walls, sourceId for openings).
    this._selectedIds = new Set();
    this.app.on("structure:selection-changed", ({ elements }) => {
      this._selectedIds = new Set((elements ?? []).map((e) => String(e.id)));
      this._draw();
    });

    this.app.on("ui:plan-overlay", ({ enabled, opacity }) => {
      if (enabled != null) this._overlayOn = enabled;
      if (opacity != null) this._overlayOpacity = opacity;
      this._drawOverlay();
    });

    this.app.on("scene:room-loaded", (room) => this._onRoom(room));
    this.app.on("scene:topology-patched", () => {
      if (!this.room || this.room.walls?.length) return;
      this._syncViewFromRoom(this.room);
    });
    this.app.on("ai:analysis", (e) => {
      if (e.phase === "result" && e.result?.spec?.rooms?.length) {
        this.namedRooms = e.result.spec.rooms;
        this._semanticsRooms = e.result.spec.semantics?.rooms ?? [];
        this._semanticsAnchors = e.result.spec.semanticsAnchors ?? [];
        this._draw();
      }
    });
    this.app.on("update", () => this._draw());
  }

  _floorPolygon(room) {
    return room?.floorPolygon ?? room?.floors?.[0]?.polygon ?? null;
  }

  _syncViewFromRoom(room) {
    if (!room?.bounds || !this.canvas) return;
    const envW = room.bounds.max[0] - room.bounds.min[0];
    const envD = room.bounds.max[2] - room.bounds.min[2];
    const aspect = envD / Math.max(envW, 0.1);
    let cw = CANVAS_MAX;
    let ch = Math.round(CANVAS_MAX * aspect);
    if (ch > CANVAS_MAX) {
      ch = CANVAS_MAX;
      cw = Math.round(CANVAS_MAX / aspect);
    }
    cw = Math.max(CANVAS_MIN_W, cw);
    ch = Math.max(CANVAS_MIN_H, ch);

    this.canvas.width = cw;
    this.canvas.height = ch;
    this.minX = room.bounds.min[0];
    this.minZ = room.bounds.min[2];
    this.scale = Math.min((cw - PAD * 2) / envW, (ch - PAD * 2) / envD);
    this.root.classList.remove("hidden");
  }

  _onRoom(room) {
    this.room = room;
    this.namedRooms = resolveNamedRooms(room);
    if (!room?.bounds || !this.canvas) return;
    this._syncViewFromRoom(room);
    this._draw();
  }

  /** @param {number} x @param {number} z */
  _w2c(x, z) {
    return [PAD + (x - this.minX) * this.scale, PAD + (z - this.minZ) * this.scale];
  }

  _wallSegments(wall, openings) {
    let spans = [{ t0: 0, t1: 1 }];
    const len = wallLen(wall);

    for (const op of openings) {
      if (op.wall !== wall.id || op.type !== "door") continue;
      const tc = paramOnWall(wall, op.center[0], op.center[2]);
      const half = (op.width ?? 0.9) / 2 / len;
      const d0 = clamp01(tc - half);
      const d1 = clamp01(tc + half);
      const next = [];
      for (const s of spans) {
        if (d1 <= s.t0 || d0 >= s.t1) {
          next.push(s);
          continue;
        }
        if (d0 > s.t0) next.push({ t0: s.t0, t1: d0 });
        if (d1 < s.t1) next.push({ t0: d1, t1: s.t1 });
      }
      spans = next;
    }
    return spans;
  }

  _drawOpenings(walls, openings) {
    const ctx = this.ctx;
    for (const op of openings) {
      const wall = walls.find((w) => w.id === op.wall);
      if (!wall) continue;
      const len = wallLen(wall);
      const tc = paramOnWall(wall, op.center[0], op.center[2]);
      const half = (op.width ?? 0.9) / 2 / len;
      const [ax, az] = wallPoint(wall, clamp01(tc - half));
      const [bx, bz] = wallPoint(wall, clamp01(tc + half));
      const [cx, cz] = this._w2c(ax, az);
      const [dx, dz] = this._w2c(bx, bz);
      const sid = op.sourceId ?? op.id;
      const selected = sid != null && this._selectedIds.has(String(sid));

      if (op.type === "door") {
        ctx.strokeStyle = selected ? SELECT_COLOR : DOOR_COLOR;
        ctx.lineWidth = selected ? 6.5 : 5;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(cx, cz);
        ctx.lineTo(dx, dz);
        ctx.stroke();

        const nx = wall.normal?.[0] ?? 0;
        const nz = wall.normal?.[1] ?? 1;
        const [mx, mz] = wallPoint(wall, tc);
        const [px, py] = this._w2c(mx, mz);
        const arcR = Math.min(10, half * len * this.scale * 0.5);
        const angle = Math.atan2(nz, nx);
        ctx.strokeStyle = "rgba(74,222,128,0.6)";
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.arc(px, py, arcR, angle - Math.PI / 2, angle);
        ctx.stroke();
      } else if (op.type === "window") {
        ctx.strokeStyle = selected ? SELECT_COLOR : WINDOW_COLOR;
        ctx.lineWidth = selected ? 4 : 2.5;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(cx, cz);
        ctx.lineTo(dx, dz);
        ctx.stroke();
      }
    }
  }

  /** Static floor plan (rooms, walls, openings, labels) — no player/camera markers. */
  _paintFloorPlan(ctx, width, height) {
    if (!this.room?.bounds) return;
    ctx.clearRect(0, 0, width, height);

    const walls = this.room.walls ?? [];
    const openings = this.room.openings ?? [];
    const { min, max } = this.room.bounds;
    const floorPoly = this._floorPolygon(this.room);

    ctx.fillStyle = FLOOR_FILL;
    if (Array.isArray(floorPoly) && floorPoly.length >= 3) {
      ctx.beginPath();
      floorPoly.forEach(([x, z], k) => {
        const [px, pz] = this._w2c(x, z);
        if (k === 0) ctx.moveTo(px, pz);
        else ctx.lineTo(px, pz);
      });
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = "rgba(255,255,255,0.22)";
      ctx.lineWidth = 1;
      ctx.stroke();
    } else {
      const [ex0, ez0] = this._w2c(min[0], min[2]);
      const [ex1, ez1] = this._w2c(max[0], max[2]);
      ctx.fillRect(ex0, ez0, ex1 - ex0, ez1 - ez0);
    }

    const labelSpots = [];
    this.namedRooms.forEach((room, ri) => {
      ctx.fillStyle = ROOM_COLORS[ri % ROOM_COLORS.length];
      const poly = Array.isArray(room.polygon) && room.polygon.length >= 3 ? room.polygon : null;
      let bx0 = Infinity;
      let bz0 = Infinity;
      let bx1 = -Infinity;
      let bz1 = -Infinity;
      if (poly) {
        ctx.beginPath();
        poly.forEach(([x, z], k) => {
          const [px, pz] = this._w2c(x, z);
          if (k === 0) ctx.moveTo(px, pz);
          else ctx.lineTo(px, pz);
          bx0 = Math.min(bx0, px);
          bz0 = Math.min(bz0, pz);
          bx1 = Math.max(bx1, px);
          bz1 = Math.max(bz1, pz);
        });
        ctx.closePath();
        ctx.fill();
      } else {
        const x = Number(room.x) || 0;
        const z = Number(room.z) || 0;
        const w = Number(room.width) || 0;
        const d = Number(room.depth) || 0;
        if (w <= 0 || d <= 0) return;
        [bx0, bz0] = this._w2c(x, z);
        [bx1, bz1] = this._w2c(x + w, z + d);
        ctx.fillRect(bx0, bz0, bx1 - bx0, bz1 - bz0);
      }
      labelSpots.push({ ri, cx: (bx0 + bx1) / 2, cz: (bz0 + bz1) / 2, w: bx1 - bx0, h: bz1 - bz0 });
    });

    for (const wall of walls) {
      const interior = String(wall.id).startsWith("w_int");
      const selected = this._selectedIds.has(String(wall.edgeId ?? wall.id));
      ctx.strokeStyle = selected ? SELECT_COLOR : interior ? WALL_INT : WALL_EXT;
      ctx.lineWidth = selected ? 3.5 : interior ? 2 : 2.5;
      ctx.lineCap = "square";
      for (const seg of this._wallSegments(wall, openings)) {
        if (isArcWall(wall)) {
          const steps = Math.max(4, Math.ceil(wallLen(wall) * this.scale / 6));
          let prev = null;
          for (let i = 0; i <= steps; i++) {
            const t = seg.t0 + (seg.t1 - seg.t0) * (i / steps);
            const [ax, az] = wallPoint(wall, t);
            const [cx, cz] = this._w2c(ax, az);
            if (prev) {
              ctx.beginPath();
              ctx.moveTo(prev[0], prev[1]);
              ctx.lineTo(cx, cz);
              ctx.stroke();
            }
            prev = [cx, cz];
          }
        } else {
          const [ax, az] = wallPoint(wall, seg.t0);
          const [bx, bz] = wallPoint(wall, seg.t1);
          const [cx, cz] = this._w2c(ax, az);
          const [dx, dz] = this._w2c(bx, bz);
          ctx.beginPath();
          ctx.moveTo(cx, cz);
          ctx.lineTo(dx, dz);
          ctx.stroke();
        }
      }
    }

    this._drawOpenings(walls, openings);

    ctx.font = "600 9px ui-sans-serif, system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    for (const spot of labelSpots) {
      const room = this.namedRooms[spot.ri];
      const anchor = anchorForRoom(room, this._semanticsAnchors);
      const line = roomMinimapLabel(room, semanticsRoomFor(this.namedRooms, this._semanticsRooms, spot.ri), anchor);
      if (!line) continue;
      const boxW = spot.w - 4;
      if (boxW < 16 || spot.h < 10) continue;
      ctx.fillStyle = "rgba(0,0,0,0.45)";
      ctx.fillText(line, spot.cx + 0.6, spot.cz + 0.6, boxW);
      ctx.fillStyle = "rgba(255,255,255,0.95)";
      ctx.fillText(line, spot.cx, spot.cz, boxW);
    }
  }

  /** PNG data URL of the built floor plan (for the 2D-render panel and plan editor). */
  exportPlanImageDataUrl(opts = {}) {
    if (!this.room?.bounds) return null;
    const maxPx = Math.max(320, Number(opts.maxPx) || 1024);
    const envW = this.room.bounds.max[0] - this.room.bounds.min[0];
    const envD = this.room.bounds.max[2] - this.room.bounds.min[2];
    if (!(envW > 0) || !(envD > 0)) return null;

    let cw = maxPx;
    let ch = Math.round(maxPx * (envD / envW));
    if (ch > maxPx) {
      ch = maxPx;
      cw = Math.round(maxPx * (envW / envD));
    }
    cw = Math.max(CANVAS_MIN_W, cw);
    ch = Math.max(CANVAS_MIN_H, ch);

    const c = document.createElement("canvas");
    c.width = cw;
    c.height = ch;
    const ctx = c.getContext("2d");
    if (!ctx) return null;

    const prev = { ctx: this.ctx, scale: this.scale, minX: this.minX, minZ: this.minZ };
    this.ctx = ctx;
    this.minX = this.room.bounds.min[0];
    this.minZ = this.room.bounds.min[2];
    this.scale = Math.min((cw - PAD * 2) / envW, (ch - PAD * 2) / envD);
    try {
      this._paintFloorPlan(ctx, cw, ch);
    } finally {
      this.ctx = prev.ctx;
      this.scale = prev.scale;
      this.minX = prev.minX;
      this.minZ = prev.minZ;
    }
    try {
      return c.toDataURL("image/png");
    } catch {
      return null;
    }
  }

  _draw() {
    if (this._overlayOn) this._drawOverlay();
    if (!this.ctx || !this.room?.bounds) return;
    const ctx = this.ctx;
    const { width, height } = this.canvas;
    this._paintFloorPlan(ctx, width, height);

    // In overhead / scene-view ("3rd person") show a camera icon at the free
    // camera's position + facing instead of the first-person player arrow.
    if (this._overhead) {
      this._drawCameraIcon();
      return;
    }

    const pos = this.camera.getLocalPosition();
    const [rawX, rawY] = this._w2c(pos.x, pos.z);
    const margin = 9;
    const px = Math.max(margin, Math.min(this.canvas.width - margin, rawX));
    const py = Math.max(margin, Math.min(this.canvas.height - margin, rawY));
    const yawRad = (this.fp.yaw * Math.PI) / 180;
    const fx = -Math.sin(yawRad);
    const fz = -Math.cos(yawRad);
    const angle = Math.atan2(fz, fx);

    ctx.save();
    ctx.translate(px, py);
    ctx.rotate(angle);
    ctx.fillStyle = "#60a5fa";
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(8, 0);
    ctx.lineTo(-5, -5);
    ctx.lineTo(-2, 0);
    ctx.lineTo(-5, 5);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();

    ctx.fillStyle = "#fff";
    ctx.beginPath();
    ctx.arc(px, py, 2.5, 0, Math.PI * 2);
    ctx.fill();
  }

  /**
   * Overhead "3rd person" camera marker: a small camera icon at the scene-view
   * camera's XZ position, rotated to its look direction, with a soft FOV cone so
   * you can read where it is and what it's pointing at on the floor plan.
   */
  _drawCameraIcon() {
    const ctx = this.ctx;
    const pos = this.camera.getLocalPosition();
    const [rawX, rawY] = this._w2c(pos.x, pos.z);

    // Keep the icon on the map even when the camera flies outside the room: pin
    // it to the nearest edge so you always see roughly where it is.
    const margin = 9;
    const px = Math.max(margin, Math.min(this.canvas.width - margin, rawX));
    const py = Math.max(margin, Math.min(this.canvas.height - margin, rawY));
    const offMap = px !== rawX || py !== rawY;

    // Facing = camera forward projected onto the floor plane (canvas y == world z).
    const fwd = this.camera.forward;
    let angle = Math.atan2(fwd.z, fwd.x);
    if (!Number.isFinite(angle) || (Math.abs(fwd.x) < 1e-4 && Math.abs(fwd.z) < 1e-4)) {
      angle = 0; // looking straight down — keep the icon upright
    }

    const ACCENT = "#fbbf24"; // amber, distinct from the blue player arrow

    // FOV cone.
    const half = 0.5; // ~29° half-angle
    const reach = 30;
    ctx.save();
    ctx.translate(px, py);
    ctx.rotate(angle);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(reach, -Math.tan(half) * reach);
    ctx.lineTo(reach, Math.tan(half) * reach);
    ctx.closePath();
    const grad = ctx.createLinearGradient(0, 0, reach, 0);
    grad.addColorStop(0, "rgba(251,191,36,0.30)");
    grad.addColorStop(1, "rgba(251,191,36,0)");
    ctx.fillStyle = grad;
    ctx.fill();
    ctx.restore();

    // Camera body + lens (top-down silhouette).
    ctx.save();
    ctx.translate(px, py);
    ctx.rotate(angle);
    const bw = 9;
    const bh = 7;
    ctx.fillStyle = ACCENT;
    ctx.strokeStyle = "rgba(0,0,0,0.55)";
    ctx.lineWidth = 1;
    // body
    ctx.beginPath();
    ctx.rect(-bw / 2 - 1.5, -bh / 2, bw, bh);
    ctx.fill();
    ctx.stroke();
    // lens barrel pointing in the look direction
    ctx.beginPath();
    ctx.moveTo(bw / 2 - 1.5, -2.2);
    ctx.lineTo(bw / 2 + 3.5, -3.4);
    ctx.lineTo(bw / 2 + 3.5, 3.4);
    ctx.lineTo(bw / 2 - 1.5, 2.2);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();

    // Position dot for a crisp anchor point.
    ctx.fillStyle = "#fff";
    ctx.beginPath();
    ctx.arc(px, py, 1.6, 0, Math.PI * 2);
    ctx.fill();

    // Edge-pinned cue: a ring shows the camera is off-map in that direction.
    if (offMap) {
      ctx.strokeStyle = "rgba(251,191,36,0.85)";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(px, py, 8, 0, Math.PI * 2);
      ctx.stroke();
    }
  }

  /**
   * Draw the rebuilt plan's wall outline onto the overlay canvas that sits atop
   * the 2D-render image, fit (contain) to the image box so structural drift is
   * directly visible. Toggled + opacity-controlled from the overlay UI.
   */
  _drawOverlay() {
    const cv = this.overlayCanvas;
    const ctx = this.overlayCtx;
    if (!cv || !ctx) return;
    if (!this._overlayOn || !this.room?.bounds) {
      cv.classList.add("hidden");
      return;
    }
    cv.classList.remove("hidden");

    const rect = cv.getBoundingClientRect();
    const W = Math.max(2, Math.round(rect.width));
    const H = Math.max(2, Math.round(rect.height));
    if (cv.width !== W) cv.width = W;
    if (cv.height !== H) cv.height = H;
    ctx.clearRect(0, 0, W, H);

    const { min, max } = this.room.bounds;
    const ew = max[0] - min[0];
    const ed = max[2] - min[2];
    if (ew <= 0 || ed <= 0) return;
    const pad = 6;
    const s = Math.min((W - pad * 2) / ew, (H - pad * 2) / ed);
    const ox = (W - ew * s) / 2;
    const oy = (H - ed * s) / 2;
    const w2 = (x, z) => [ox + (x - min[0]) * s, oy + (z - min[2]) * s];

    ctx.globalAlpha = this._overlayOpacity;
    ctx.strokeStyle = "#fb923c";
    ctx.lineWidth = 1.5;
    ctx.lineCap = "round";
    for (const wall of this.room.walls ?? []) {
      const [ax, az] = w2(wall.start[0], wall.start[1]);
      const [bx, bz] = w2(wall.end[0], wall.end[1]);
      ctx.beginPath();
      ctx.moveTo(ax, az);
      ctx.lineTo(bx, bz);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }
}

export default Minimap;
